import React, { FunctionComponent, useContext, useEffect } from 'react';
import { Loader } from 'components/Loader';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { useSelector } from 'react-redux';
import { usePageLoaded } from 'hooks/usePageLoaded';
import { SEOHeaderTags } from 'components/SEO/SEOHeaderTags';
import { Error404 } from 'components/ErrorPages/Error404';
import { useAppSelector } from 'hooks/redux';
import { WebSocketContext } from 'features/hub/hub';
import { RootState } from 'rootReducer';
import { Crowd } from 'features/crowd/Crowd';
import { ChatMenu } from '../ChannelChat/ChatMenu';
import { useNavigate } from 'react-router-dom';
import { nextLiveBroadcastSelector } from 'features/broadcast/selectors';

export const ChannelCrowd: FunctionComponent = () => {
  const channelId = useAppSelector(
    (state) => state.channel.channelDetails?.ownerId
  );
  const { isNativeAppWebview, isMobileAppWebview, isDACreatorApp } =
    useContext(UserAgentContext);
  const { joinCrowd } = useContext(WebSocketContext);
  const currentState = useAppSelector((state) => state);
  const liveBroadcast = nextLiveBroadcastSelector(currentState);
  const navigate = useNavigate();

  // Hub socket states
  const isHubConnected = useSelector(
    (state: RootState) => state.hub.isHubConnected
  );
  const isHubCrowdConnectedUnauthenticated = useSelector(
    (state: RootState) => state.hub.isHubCrowdConnectedUnauthenticated
  );
  const isHubCrowdConnectedAuthenticated = useSelector(
    (state: RootState) => state.hub.isHubCrowdConnectedAuthenticated
  );
  const isHubAuthenticated = useSelector(
    (state: RootState) => state.hub.isHubAuthenticated
  );

  usePageLoaded();

  useEffect(
    function redirectToChatIfNotLive() {
      if (isDACreatorApp && !liveBroadcast?.isLive) {
        navigate(`/chat`, { replace: true });
      }
    },
    [isDACreatorApp, liveBroadcast, navigate]
  );

  // Join Hub crowd for native apps
  useEffect(() => {
    if (
      isNativeAppWebview &&
      isHubConnected &&
      !isHubCrowdConnectedUnauthenticated &&
      !isHubCrowdConnectedAuthenticated
    ) {
      joinCrowd(isHubAuthenticated);
    }
  }, [
    isNativeAppWebview,
    isHubConnected,
    isHubCrowdConnectedUnauthenticated,
    isHubCrowdConnectedAuthenticated,
    isHubAuthenticated,
    joinCrowd
  ]);

  // Upgrade Hub crowd to authenticated
  useEffect(() => {
    if (
      isNativeAppWebview &&
      isHubAuthenticated &&
      isHubCrowdConnectedUnauthenticated &&
      !isHubCrowdConnectedAuthenticated
    ) {
      joinCrowd(true);
    }
  }, [
    isNativeAppWebview,
    isHubAuthenticated,
    isHubCrowdConnectedUnauthenticated,
    isHubCrowdConnectedAuthenticated,
    joinCrowd
  ]);

  // Join crowd for native apps (now using same socket as web)
  useEffect(() => {
    if (
      isNativeAppWebview &&
      isHubConnected &&
      !isHubCrowdConnectedUnauthenticated &&
      !isHubCrowdConnectedAuthenticated
    ) {
      joinCrowd(isHubAuthenticated);
    }
  }, [
    isNativeAppWebview,
    isHubConnected,
    isHubCrowdConnectedUnauthenticated,
    isHubCrowdConnectedAuthenticated,
    isHubAuthenticated,
    joinCrowd
  ]);

  // Upgrade crowd to authenticated for native apps
  useEffect(() => {
    if (
      isNativeAppWebview &&
      isHubAuthenticated &&
      isHubCrowdConnectedUnauthenticated &&
      !isHubCrowdConnectedAuthenticated
    ) {
      joinCrowd(true);
    }
  }, [
    isNativeAppWebview,
    isHubAuthenticated,
    isHubCrowdConnectedUnauthenticated,
    isHubCrowdConnectedAuthenticated,
    joinCrowd
  ]);

  if (!channelId) {
    return <Loader fillComponent={true} hideOnNative={isMobileAppWebview} />;
  }

  if (isNativeAppWebview) {
    return (
      <>
        <SEOHeaderTags title={'Crowd'} description={''} image={''} url={''} />
        <div className="tw-bg-red tw-flex tw-flex-col tw-h-[300px] tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-bottom-0 tw-text-label [.creator-da_&]:tw-bg-white [.ios-creator_&]:tw-pb-safe [.ios-creator_&]:tw-pt-safe">
          <ChatMenu />
          <Crowd fullScreen={true} />
        </div>
      </>
    );
  }

  return <Error404 />;
};
